import { useMutation, useQuery } from '@tanstack/react-query';
import { sharedQueryKeys } from '~/api/utils/sharedQueryKeys';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionGeoData } from '~/shared/types/session';
import { User } from '~/shared/types/user';
import { Empty } from '~/shared/types/utility';
import { emitCsrfTokenChange, getCsrfTokenFromCookie } from '~/utils/csrf-token-utils';
import { api, appAxios } from './utils/api-base';
import eventEmitter from '~/utils/events/event-emitter';

export const useSessionUser = () => {
  return useQuery({
    queryKey: [sharedQueryKeys.session.user],
    queryFn: async () => {
      const response = await api.get<User | Empty>('/session/session_user');
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5, // poll 
    //refetchOnWindowFocus: true, // adds a lot of requests to server
  }
  );
};

export const useSessionGeoData = () => {
  return useQuery({
    queryKey: [sharedQueryKeys.session.geoData],
    queryFn: async () => {
      const response = await api.get<SessionGeoData | Empty>('/session/geo_data');
      return response.data;
    },
  }
  );
};

export const useSignInUser = () => {
  return useMutation({
    mutationFn: async ({
      email,
      password,
      siteId,
    }: {
      email: string
      password: string
      siteId: number | undefined
    }) => {
      const response = await appAxios.post<User | { error: string }>('/authentication/sign_in', {
        user: { email, password, site_id: siteId }
      });
      return response.data;
    },
  })
}

export const useConfirmUserEmail = () => {
  return useMutation({
    mutationFn: async (confirmation_token: string) => {
      const response = await appAxios.get<User | { error: string }>('/authentication/confirmation', {
        params: { confirmation_token }
      });
      return response.data;
    },
  })
}

export const useResendConfirmationEmail = () => {
  return useMutation({
    mutationFn: async ({ email, siteId }: { email: string, siteId: number | undefined }) => {
      const response = await appAxios.post<User | { errors: { key: string } }>('/authentication/confirmation', {
        user: { email, site_id: siteId }
      });
      return response.data;
    },
  })
}

export const useResetPasswordRequest = () => {
  return useMutation({
    mutationFn: async ({ email, siteId }: { email: string, siteId: number | undefined }) => {
      const response = await appAxios.post<User | { errors: { key: string } }>('/authentication/password', {
        user: { email, site_id: siteId }
      });
      return response.data;
    },
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async ({
      reset_password_token,
      password,
      passwordConfirmation
    }: {
      reset_password_token: string
      password: string
      passwordConfirmation: string
    }) => {
      const response = await appAxios.put<User | { errors: { key: string } }>('/authentication/password', {
        user: { reset_password_token, password, password_confirmation: passwordConfirmation }
      });
      return response.data;
    },
  })
}

export const useSignUpUser = () => {
  return useMutation({
    mutationFn: async ({
      siteId,
      email,
      password,
      password_confirmation,
      first_name,
      last_name,
    }: {
      siteId: number | undefined
      email: string
      password: string
      password_confirmation: string
      first_name: string
      last_name: string
    }) => {
      const response = await appAxios.post<User | { errors: { key: string } }>('/authentication', {
        user: {
          site_id: siteId,
          first_name,
          last_name,
          email,
          password,
          password_confirmation
        }
      });
      return response.data;
    },
  })
}

export function useSignOut() {
  const navigate = useNavigate();

  const signOut = useMemo(
    () => (
      async () => {
        try {
          await appAxios.delete('/authentication/sign_out')
          emitCsrfTokenChange()
          eventEmitter.emit('signOutSuccessful');
        } catch (signOutError) {
          // swollowing this because technically the Devise is sending a redirect request
          // which axios will try follow and fail
          console.log({ signOutError });
        }
        // token has changed after signout
      }),
    [navigate]
  );

  return signOut;
}